import { Booking } from './booking';
import { Port } from './port';
import { Sailing } from './sailing';
import { Vehicle } from './vehicle';
import { VehicleType } from './vehicle-type';
import { ShippingLine } from './shipping-line';
import { Route } from './route';

export class BookingGroup {

    constructor() {
        this.is_return_journey = false;
        this.outbound_booking = Booking.createFromObject({});
        this.return_booking = Booking.createFromObject({});
    }

    private _is_return_journey: boolean = false;

    get is_return_journey(): boolean {
        return this._is_return_journey;
    }

    set is_return_journey(isReturn: boolean) {
        this._is_return_journey = isReturn;

        if (this.is_return_journey) {
            this.return_booking.arrival_port = this.outbound_booking.departure_port;
            this.return_booking.departure_port = this.outbound_booking.arrival_port;
        }
    }

    private _route: Route;

    get route(): Route {
        return this._route;
    }

    set route(route: Route) {
        this._route = route;
        if (route === undefined) return;

        let departurePort = route.getDeparturePort();
        let destinationPort = route.getDestinationPort();

        if (departurePort !== undefined)
            this.departure_port = departurePort;
        if (destinationPort !== undefined)
            this.arrival_port = destinationPort;
    }

    private _outbound_booking: Booking;

    get outbound_booking(): Booking {
        return this._outbound_booking;
    }

    set outbound_booking(booking: Booking) {
        this._outbound_booking = booking;
    }


    private _return_booking: Booking;

    get return_booking(): Booking {
        return this._return_booking;
    }

    set return_booking(booking: Booking) {
        this._return_booking = booking;
    }


    private _departure_port: Port;

    get departure_port(): Port {
        return this._departure_port;
    }

    set departure_port(port: Port) {
        this._departure_port = port;

        this.outbound_booking.departure_port = port;
        if (this.is_return_journey)
            this.return_booking.arrival_port = port;
    }


    private _arrival_port: Port;

    get arrival_port(): Port {
        return this._arrival_port;
    }

    set arrival_port(port: Port) {
        this._arrival_port = port;

        this.outbound_booking.arrival_port = port;
        if (this.is_return_journey)
            this.return_booking.departure_port = port;
    }


    private _outbound_sailing: Sailing;

    get outbound_sailing(): Sailing {
        return this._outbound_sailing;
    }

    set outbound_sailing(sailing: Sailing) {
        this._outbound_sailing = sailing;
        if (sailing === undefined || this._outbound_booking === undefined) return;
        this._outbound_booking.shipping_line = this._outbound_sailing.shipping_line;
        this._outbound_booking.arriving_at = this._outbound_sailing.arriving_at;
        this._outbound_booking.departing_at = this._outbound_sailing.departing_at;
    }

    private _return_sailing: Sailing;

    get return_sailing(): Sailing {
        return this._return_sailing;
    }

    set return_sailing(sailing: Sailing) {
        this._return_sailing = sailing;
        if (sailing === undefined || this._outbound_booking === undefined) return;
        this._return_booking.shipping_line = this._return_sailing.shipping_line;
        this._return_booking.arriving_at = this._return_sailing.arriving_at;
        this._return_booking.departing_at = this._return_sailing.departing_at;
    }

    private _vehicle: Vehicle;

    get vehicle(): Vehicle {
        return this._vehicle;
    }

    set vehicle(vehicle: Vehicle) {

        this._vehicle = vehicle;

        if (vehicle !== undefined) {
            let vehicle_type: VehicleType = vehicle.type;
            if (vehicle_type === undefined) return;
            this.vehicle_type = vehicle_type;
        }

        let booking: Booking = this.outbound_booking;
        booking.vehicle = vehicle;
        this.outbound_booking = booking;

        booking = this.return_booking;
        booking.vehicle = vehicle;
        this.return_booking = booking;

    }

    private _shipping_line: ShippingLine;

    get shipping_line(): ShippingLine {
        return this._shipping_line;
    }

    set shipping_line(shippingLine: ShippingLine) {
        this._shipping_line = shippingLine;
        this._outbound_booking.shipping_line = shippingLine;
        if (this.requiresReturnSailing() && this.hasReturnSailing())
            this._return_booking.shipping_line = shippingLine;
    }

    private _vehicle_type: VehicleType;

    get vehicle_type(): VehicleType {
        return this._vehicle_type;
    }

    set vehicle_type(vehicleType: VehicleType) {
        this._vehicle_type = vehicleType;

        let booking: Booking = this.outbound_booking;
        booking.vehicle_type = vehicleType;
        this.outbound_booking = booking;

        booking = this.return_booking;
        booking.vehicle_type = vehicleType;
        this.return_booking = booking;
    }

    get vehicle_reg(): string {
        if (this.vehicle === undefined) this.vehicle = new Vehicle();
        return this.vehicle.vehicle_reg;
    }

    set vehicle_reg(vehicleReg: string) {
        let vehicle = this.vehicle;
        if (vehicle === undefined) vehicle = new Vehicle();
        vehicle.vehicle_reg = vehicleReg;
        this.vehicle = vehicle;
    }

    get trailer_reg(): string {
        if (this.vehicle === undefined) this.vehicle = new Vehicle();
        return this.vehicle.trailer_reg;
    }

    set trailer_reg(trailerReg: string) {
        let vehicle = this.vehicle;
        if (vehicle === undefined) vehicle = new Vehicle();
        vehicle.trailer_reg = trailerReg;
        this.vehicle = vehicle;
    }

    get vehicle_length(): number {
        if (this.vehicle === undefined) this.vehicle = new Vehicle();
        return this.vehicle.length;
    }

    set vehicle_length(vehicle_length: number) {
        let vehicle = this.vehicle;
        if (vehicle === undefined) vehicle = new Vehicle();
        if (vehicle.length === undefined) vehicle.length = 0;
        vehicle.length = vehicle_length;
        this.vehicle = vehicle;
    }

    get vehicle_width(): number {
        if (this.vehicle === undefined) this.vehicle = new Vehicle();
        return this.vehicle.width;
    }

    set vehicle_width(vehicle_width: number) {
        let vehicle = this.vehicle;
        if (vehicle === undefined) vehicle = new Vehicle();
        vehicle.width = vehicle_width;
        this.vehicle = vehicle;
    }

    get vehicle_height(): number {
        if (this.vehicle === undefined) this.vehicle = new Vehicle();
        return this.vehicle.height;
    }

    set vehicle_height(vehicle_height: number) {
        let vehicle = this.vehicle;
        if (vehicle === undefined) vehicle = new Vehicle();
        vehicle.height = vehicle_height;
        this.vehicle = vehicle;
    }

    get vehicle_weight(): number {
        if (this.vehicle === undefined) this.vehicle = new Vehicle();
        return this.vehicle.weight;
    }

    set vehicle_weight(vehicle_weight: number) {
        let vehicle = this.vehicle;
        if (vehicle === undefined) vehicle = new Vehicle();
        vehicle.weight = vehicle_weight;
        this.vehicle = vehicle;
    }

    get vehicle_weight_formatted(): String {
        if (this.vehicle === undefined)
            return 'Unknown';
        return this.vehicle.weight_formatted;
    }

    get loaded(): boolean {
        return this.outbound_booking.loaded;
    }

    set loaded(isLoaded: boolean) {
        this.outbound_booking.loaded = isLoaded;
        this.return_booking.loaded = isLoaded;
    }

    get live_stock(): boolean {
        return this.outbound_booking.live_stock;
    }

    set live_stock(hasLiveStock: boolean) {
        this.outbound_booking.live_stock = hasLiveStock;
        this.return_booking.live_stock = hasLiveStock;
    }

    get hazardous(): boolean {
        return this.outbound_booking.hazardous;
    }

    set hazardous(isHazardous: boolean) {
        this.outbound_booking.hazardous = isHazardous;
        this.return_booking.hazardous = isHazardous;
    }

    get plug_in(): boolean {
        return this.outbound_booking.plug_in;
    }

    set plug_in(hasPlugin: boolean) {
        this.outbound_booking.plug_in = hasPlugin;
        this.return_booking.plug_in = hasPlugin;
    }

    get main_deck(): boolean {
        return this.outbound_booking.main_deck;
    }

    set main_deck(hasMainDeck: boolean) {
        this.outbound_booking.main_deck = hasMainDeck;
        this.return_booking.main_deck = hasMainDeck;
    }

    get drivers(): number {
        return this.outbound_booking.drivers;
    }

    set drivers(drivers: number) {
        if (this.vehicle_type !== undefined && this.vehicle_type.drivers !== undefined) {
            if (drivers < this.vehicle_type.drivers.min)
                drivers = this.vehicle_type.drivers.min;
            if (drivers > this.vehicle_type.drivers.max)
                drivers = this.vehicle_type.drivers.max;
        }
        this.outbound_booking.drivers = drivers;
        this.return_booking.drivers = drivers;
    }

    get contact_name(): string {
        return this.outbound_booking.contact_name;
    }

    set contact_name(name: string) {
        this.outbound_booking.contact_name = name;
        this.return_booking.contact_name = name;
    }

    get contact_number(): string {
        return this.outbound_booking.contact_number;
    }

    set contact_number(contactNumber: string) {
        this.outbound_booking.contact_number = contactNumber;
        this.return_booking.contact_number = contactNumber;
    }

    get contact_email(): string {
        return this.outbound_booking.contact_email;
    }

    set contact_email(contactEmail: string) {
        this.outbound_booking.contact_email = contactEmail;
        this.return_booking.contact_email = contactEmail;
    }

    get notes(): string {
        return this.outbound_booking.notes;
    }

    set notes(notes: string) {
        this.outbound_booking.notes = notes;
        this.return_booking.notes = notes;
    }

    hasAllSailings(): boolean {
        if (!this.hasFirstSailing()) return false;
        if (!this.requiresReturnSailing()) return true;
        return this.hasReturnSailing();
    }

    hasFirstSailing(): boolean {
        return this.outbound_sailing !== undefined;
    }

    requiresReturnSailing(): boolean {
        return this.is_return_journey;
    }

    hasReturnSailing(): boolean {
        return this._return_sailing !== undefined;
    }
}




// WEBPACK FOOTER //
// ./src/entities/booking-group.ts